import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ideasAPI, statsAPI } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Filter, Eye, ThumbsUp, Clock, Sparkles } from 'lucide-react';

export const BrowseIdeasPage = () => {
  const [ideas, setIdeas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all'
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadIdeas();
  }, [filters.category, filters.status]);

  const loadCategories = async () => {
    try {
      const response = await statsAPI.getCategories();
      setCategories(response.data.idea_categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadIdeas = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category && filters.category !== 'all') params.category = filters.category;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      
      const response = await ideasAPI.getAll(params);
      setIdeas(response.data);
    } catch (error) {
      console.error('Failed to load ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadIdeas();
  };

  const getCategoryClass = (category) => {
    const classes = {
      'Tech': 'category-tech',
      'Retail': 'category-retail',
      'Food & Beverage': 'category-food',
      'Services': 'category-services',
      'Healthcare': 'category-healthcare',
      'Education': 'category-education',
      'Finance': 'category-finance',
      'Entertainment': 'category-entertainment'
    };
    return classes[category] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="browse-ideas-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">
          Browse Business Ideas
        </h1>
        <p className="text-slate-600 mt-2">
          Explore community business ideas and contribute your suggestions.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search ideas..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select 
              value={filters.category} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.status} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="planned">With Plan</SelectItem>
              </SelectContent>
            </Select>

            <Button type="submit" data-testid="search-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : ideas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No ideas found</h3>
            <p className="text-slate-600">Try adjusting your filters or search terms.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ideas.map((idea) => (
            <Link 
              key={idea.id} 
              to={`/ideas/${idea.id}`}
              className="group"
              data-testid={`idea-card-${idea.id}`}
            >
              <Card className="h-full card-hover overflow-hidden">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <Badge className={getCategoryClass(idea.category)}>
                      {idea.category}
                    </Badge>
                    {idea.status === 'planned' && (
                      <Sparkles className="w-4 h-4 text-indigo-500" />
                    )}
                  </div>
                  
                  <h3 className="font-heading font-semibold text-lg text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-2 mb-2">
                    {idea.title}
                  </h3>
                  
                  <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                    {idea.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-slate-500 pt-4 border-t border-slate-100">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> {idea.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <ThumbsUp className="w-4 h-4" /> {idea.upvotes}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> 
                      {new Date(idea.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default BrowseIdeasPage;
